import React, { useState, useRef, useEffect } from 'react';
import Button from '../Button';

interface InlineEditBaseProps {
  value: string | number | undefined;
  onSave: (value: string | number) => Promise<void> | void;
  onCancel?: () => void;
  children: (props: {
    isEditing: boolean;
    value: string | number;
    onChange: (value: string | number) => void;
    onSave: () => void;
    onCancel: () => void;
    inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;
  }) => React.ReactNode;
  disabled?: boolean;
  className?: string;
  editButtonClassName?: string;
  showEditButton?: boolean;
}

const InlineEditBase: React.FC<InlineEditBaseProps> = ({
  value,
  onSave,
  onCancel,
  children,
  disabled = false,
  className = '',
  editButtonClassName = '',
  showEditButton = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<string | number>(value || '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(null);

  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current instanceof HTMLInputElement || inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (disabled) return;
    setEditValue(value || '');
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      // Keep editing mode on error
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value || '');
    setIsEditing(false);
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div className={`inline-edit-wrapper ${className}`}>
        {children({
          isEditing: true,
          value: editValue,
          onChange: setEditValue,
          onSave: handleSave,
          onCancel: handleCancel,
          inputRef,
        })}
        <div className="flex items-center gap-2 mt-2 animate-fade-in">
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isSaving}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`inline-edit-display group ${className}`}>
      <div className="inline-edit-content">{value || <span className="text-[var(--color-neutral-400)] italic">Not set</span>}</div>
      {!disabled && showEditButton && (
        <button
          type="button"
          onClick={handleStartEdit}
          className={`opacity-0 group-hover:opacity-100 transition-opacity duration-200 ml-2 ${editButtonClassName}`}
          aria-label="Edit"
        >
          <svg className="w-4 h-4 text-[var(--color-neutral-400)] hover:text-[var(--color-neutral-600)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InlineEditBase;

